import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { TableCell, TableRow } from '@mui/material';
import {
    makeRentalBookingDetailsUrl,
    paths,
} from '../../routes';
import { useCanViewAllTenants } from '../../common/useCanViewAllTenants';
import { useAccessibleTenants } from '../../common/useAccessibleTenants';
import { getTenantDisplayName } from '../../common/tenantDisplayNames';
import { useDispatch } from 'react-redux';
import { MasterBooking } from '../../api/master/masterApiTypes';
import { masterApiSlice, useGetMasterBookingsQuery } from '../../api/master/masterApiSlice';
import { formatDateTime } from '../../utils';
import MasterListScaffold, { MasterColumn } from './MasterListScaffold';
import { buildBookingNumberLetterMap } from '../../utils/bookingNumberLetters';
import { useMasterListState } from './useMasterListState';
import { useMasterTenantData } from './useMasterTenantData';
import { finishTimingLabelKey, getFinishTiming } from '../../utils/bookingFinishTiming';
import { onRowClick } from '../../utils/rowClick';

const ROWS_PER_PAGE_OPTIONS = [100, 200, 300, 500];
const ROWS_PER_PAGE_STORAGE_KEY = 'master_bookings_rows_per_page';

const PAST_WINDOW_DAYS = 30;

const parseTime = (value: string | null): number | null => {
    if (!value) return null;
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
};

const isInDefaultWindow = (row: MasterBooking, nowMillis: number): boolean => {
    const cutoff = nowMillis - PAST_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    const reference = parseTime(row.endDate) ?? parseTime(row.startDate);
    // Bookings without parseable dates are kept (defensive — pending /
    // open-ended bookings shouldn't disappear silently).
    if (reference === null) return true;
    return reference >= cutoff;
};

/**
 * Default ordering puts the bookings needing customer-service attention
 * first: overtime (still running past the booked end — the backend
 * derives this status for sup/rental) → active → booked/reserved →
 * finished → everything else (canceled, blocked, conflicted, …). Within
 * each category the existing imminence sort applies. Applies to all
 * tenants/use cases; column-header sorting overrides it.
 */
const STATUS_CATEGORY_ORDER: Record<string, number> = {
    overtime: 0,
    active: 1,
    booked: 2,
    reserved: 2,
    finished: 3,
};

const categoryRank = (status: string): number =>
    STATUS_CATEGORY_ORDER[status.toLowerCase()] ?? 4;

/**
 * Sort by imminence: current and future bookings ascending (soonest
 * first), with past bookings pinned to the bottom in descending end-date
 * order (most-recently-ended first).
 */
const compareByImminence = (a: MasterBooking, b: MasterBooking, nowMillis: number): number => {
    const aStart = parseTime(a.startDate);
    const bStart = parseTime(b.startDate);
    const aIsFuture = aStart !== null && aStart >= nowMillis;
    const bIsFuture = bStart !== null && bStart >= nowMillis;
    if (aIsFuture && bIsFuture) return (aStart as number) - (bStart as number);
    if (aIsFuture) return -1;
    if (bIsFuture) return 1;
    const aEnd = parseTime(a.endDate) ?? aStart ?? 0;
    const bEnd = parseTime(b.endDate) ?? bStart ?? 0;
    return bEnd - aEnd;
};

/**
 * Same color map as `BookingTable.tsx` for visual consistency across the
 * admin UI. Keys are normalized lower-case status strings (we compare
 * case-insensitively per the project rule). Unknown statuses fall back
 * to plain text without a pill — no crashes on unexpected values.
 */
const STATUS_COLORS: Record<string, { bg: string, text: string, dot: string }> = {
    active: { bg: '#CFD8FE', text: '#3F66FB', dot: '#3F66FB' },
    // Same amber as the shared StatusBadge's overtime entry (booking details).
    overtime: { bg: '#FEF3C7', text: '#B45309', dot: '#B45309' },
    booked: { bg: '#CEEFCE', text: '#2D912D', dot: '#2D912D' },
    canceled: { bg: '#F8DFE5', text: '#E94F54', dot: '#E94F54' },
    finished: { bg: '#DEDFDF', text: '#515557', dot: '#515557' },
    reserved: { bg: '#EDE9FE', text: '#7C3AED', dot: '#7C3AED' },
    incompleted: { bg: '#FEF3C7', text: '#D97706', dot: '#D97706' },
    blocked: { bg: '#FEE2E2', text: '#991B1B', dot: '#991B1B' },
    conflicted: { bg: '#FCE7F3', text: '#9D174D', dot: '#9D174D' },
};


/**
 * Full booking period including time of day. Start/end times matter for
 * customer service across all services with time ranges — SUP slots
 * (e.g. 15:00–16:00), apartment check-in/check-out (e.g. 15:00 until
 * 11:00 the next day), venue slots — so the time is always rendered
 * alongside the date.
 */
const formatDateRange = (
    startDate: string | null,
    endDate: string | null,
    language: 'de' | 'en',
    fallback: string,
): string => {
    if (!startDate && !endDate) return fallback;
    const start = startDate
        ? formatDateTime(startDate, language, { includeTime: true })
        : fallback;
    const end = endDate
        ? formatDateTime(endDate, language, { includeTime: true })
        : fallback;
    return `${start} – ${end}`;
};

const MasterBookingsPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const language: 'de' | 'en' = i18n.language === 'de' ? 'de' : 'en';
    const canViewAllTenants = useCanViewAllTenants();
    const accessibleTenants = useAccessibleTenants();
    const list = useMasterListState({
        accessibleTenants,
        rowsPerPageStorageKey: ROWS_PER_PAGE_STORAGE_KEY,
        defaultSort: { key: '', order: '' },
        rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
    });

    const { rowsByTenant, failedTenants, loadedTenants, querySlots } =
        useMasterTenantData(accessibleTenants, useGetMasterBookingsQuery);

    // Reload = invalidate every tenant's cached booking list; the mounted
    // query slots refetch and the rows update in place.
    const dispatch = useDispatch();
    const handleReload = React.useCallback(() => {
        dispatch(masterApiSlice.util.invalidateTags(['masterBookings']));
    }, [dispatch]);

    /**
     * Map useCase → localized service label. Matches the mapping used in
     * BookingsPage.tsx (`apartment` → `room_rental`, `venue` →
     * `venue_reservation`). Unknown / empty use cases fall through to
     * `common.not_applicable` at the call site.
     */
    const getServiceLabel = React.useCallback((useCase: string): string => {
        const normalized = useCase.toLowerCase();
        const map: Record<string, string> = {
            apartment: 'bookings.bookings_table.filter_options.room_rental',
            venue: 'bookings.bookings_table.filter_options.venue_reservation',
            hall: 'bookings.bookings_table.filter_options.hall',
            rental: 'bookings.bookings_table.filter_options.rental',
            sup: 'bookings.bookings_table.filter_options.sup',
            p2p: 'bookings.bookings_table.filter_options.p2p',
            storage: 'bookings.bookings_table.filter_options.storage',
            parcel: 'bookings.bookings_table.filter_options.parcel',
        };
        const key = map[normalized];
        return key ? t(key) : '';
    }, [t]);

    const mergedRows = useMemo(() => {
        const nowMillis = Date.now();
        const all: MasterBooking[] = [];
        accessibleTenants.forEach((tenant) => {
            if (!list.selectedTenantSlugs.has(tenant)) return;
            const rows = rowsByTenant[tenant];
            if (rows) all.push(...rows);
        });
        return all
            // The past window only prunes concluded bookings (finished,
            // canceled, …). Overtime/active rows are still running — they
            // must stay visible no matter how old their booked end is.
            .filter((row) =>
                categoryRank(row.status) <= 1 || isInDefaultWindow(row, nowMillis))
            .sort((a, b) =>
                categoryRank(a.status) - categoryRank(b.status)
                || compareByImminence(a, b, nowMillis));
    }, [rowsByTenant, accessibleTenants, list.selectedTenantSlugs]);

    // Multi-order purchases (e.g. SUP quantity > 1) share one persisted
    // booking number; derive the display A/B/… letters exactly like the
    // per-tenant bookings list, the end-user app and the confirmation
    // emails — otherwise sibling boards look like accidental duplicates.
    // Group per tenant so equal numbers of different tenants stay apart.
    const displayNumberById = useMemo(() =>
        buildBookingNumberLetterMap(mergedRows, {
            getId: (row) => `${row.tenantSlug}-${row.id}`,
            getBookingNumber: (row) => row.bookingNumber,
            getGroupKey: (row) => `${row.tenantSlug}:${row.bookingNumber}`,
        }), [mergedRows]);

    const getDisplayBookingNumber = React.useCallback(
        (row: MasterBooking): string =>
            displayNumberById.get(`${row.tenantSlug}-${row.id}`)
            ?? row.bookingNumber,
        [displayNumberById]);

    const filteredRows = useMemo(() => {
        const term = list.searchQuery.trim().toLowerCase();
        if (!term) return mergedRows;
        return mergedRows.filter((row) => {
            const tenantName = getTenantDisplayName(row.tenantSlug).toLowerCase();
            const bookingNumber = getDisplayBookingNumber(row).toLowerCase();
            const customer = row.customerName.toLowerCase();
            const status = row.status.toLowerCase();
            const service = getServiceLabel(row.useCase).toLowerCase();
            return (
                tenantName.includes(term) ||
                bookingNumber.includes(term) ||
                customer.includes(term) ||
                status.includes(term) ||
                service.includes(term)
            );
        });
    }, [mergedRows, list.searchQuery, getServiceLabel, getDisplayBookingNumber]);

    const sortedRows = useMemo(() => {
        const compare = (a: MasterBooking, b: MasterBooking): number => {
            switch (list.sortState.key) {
                case 'tenant':
                    return getTenantDisplayName(a.tenantSlug)
                        .localeCompare(getTenantDisplayName(b.tenantSlug));
                case 'booking':
                    return getDisplayBookingNumber(a)
                        .localeCompare(getDisplayBookingNumber(b));
                case 'dates': {
                    const aRef = parseTime(a.startDate) ?? parseTime(a.endDate) ?? 0;
                    const bRef = parseTime(b.startDate) ?? parseTime(b.endDate) ?? 0;
                    return aRef - bRef;
                }
                case 'customer':
                    return a.customerName.localeCompare(b.customerName);
                case 'service':
                    return getServiceLabel(a.useCase).localeCompare(getServiceLabel(b.useCase));
                case 'status':
                    return a.status.toLowerCase().localeCompare(b.status.toLowerCase());
                case 'issue':
                    return Number(a.hasDamageReport) - Number(b.hasDamageReport);
                default:
                    return 0;
            }
        };
        return list.applySort(filteredRows, compare);
    }, [filteredRows, list.applySort, list.sortState.key, getServiceLabel, getDisplayBookingNumber]);

    const pagedRows = useMemo(
        () => list.paginate(sortedRows),
        [sortedRows, list.paginate],
    );

    const handleRowClick = React.useCallback((row: MasterBooking) => {
        // BookingDetailsPage is registered at paths.rentalBookingDetails
        // and acts as the dispatcher for all use cases (apartment, venue,
        // rental, sup, etc.) — it inspects `useCase` internally to render
        // the right variant. The legacy paths.bookingDetails route is not
        // wired in App.tsx, so we always use the rental URL builder.
        list.drillIntoTenant(row.tenantSlug, makeRentalBookingDetailsUrl(row.id));
    }, [list.drillIntoTenant]);

    if (!canViewAllTenants) {
        return <Navigate to={paths.unauthorized} replace />;
    }

    const total = accessibleTenants.length;
    const loaded = loadedTenants.size;
    const failedCount = failedTenants.size;
    const notApplicable = t('common.not_applicable');
    const allLoaded = total > 0 && loaded === total;
    const showEmptyState =
        allLoaded && failedCount === 0 && sortedRows.length === 0;

    const renderPill = (label: string, colors: { bg: string, text: string, dot: string }) => (
        <span
            className={
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium'
            }
            style={{ backgroundColor: colors.bg, color: colors.text }}
        >
            <span
                className={'inline-block rounded-full'}
                style={{
                    width: 6,
                    height: 6,
                    backgroundColor: colors.dot,
                }}
            />
            {label}
        </span>
    );

    const renderStatusPill = (row: MasterBooking) => {
        const status = row.status;
        if (!status) return <span>{notApplicable}</span>;
        const colors = STATUS_COLORS[status.toLowerCase()];
        // Finished bookings with a real finish timestamp get a small
        // annotation below the badge: finished within the booked time
        // frame or in overtime. Derived by the shared helper so the
        // per-tenant bookings table shows exactly the same thing.
        const timing = getFinishTiming(status, row.finishedAtDate, row.endDate);
        const pill = colors ? renderPill(status, colors) : <span>{status}</span>;
        if (!timing) return pill;
        return (
            <span className={'inline-flex flex-col items-start gap-0.5'}>
                {pill}
                <span className={'text-xs text-foreground/50'}>
                    {t(finishTimingLabelKey(timing))}
                </span>
            </span>
        );
    };

    // Mirrors the tenant-level BookingTable's issue badge: same yes/no
    // labels and the shared StatusBadge's yes/no colors.
    const renderIssuePill = (hasDamageReport: boolean) =>
        hasDamageReport
            ? renderPill(
                t('booking_details.status_badge.booking_status.yes'),
                { bg: '#CEEFCE', text: '#2D912D', dot: '#2D912D' },
            )
            : renderPill(
                t('booking_details.status_badge.booking_status.no'),
                { bg: '#F8DFE5', text: '#E94F54', dot: '#E94F54' },
            );

    const columns: MasterColumn[] = [
        { label: t('master.columns.tenant'), sortKey: 'tenant' },
        { label: t('master.columns.booking_number'), sortKey: 'booking' },
        { label: t('master.columns.service'), sortKey: 'service' },
        { label: t('master.columns.dates'), sortKey: 'dates' },
        { label: t('master.columns.customer'), sortKey: 'customer' },
        { label: t('master.columns.status'), sortKey: 'status' },
        { label: t('master.columns.issue'), sortKey: 'issue' },
    ];

    return (
        <MasterListScaffold
            controls={list}
            title={t('master.bookings')}
            subtitle={t('master.bookings_count', { count: sortedRows.length })}
            searchPlaceholder={t('master.search_placeholder')}
            columns={columns}
            totalCount={sortedRows.length}
            loadedTenants={loaded}
            totalTenants={total}
            failedTenants={Array.from(failedTenants)}
            showEmptyState={showEmptyState}
            emptyMessage={t('master.empty')}
            querySlots={querySlots}
            onReload={handleReload}
        >
            {pagedRows.map((row) => {
                const serviceLabel = getServiceLabel(row.useCase);
                return (
                    <TableRow
                        key={`${row.tenantSlug}-${row.id}`}
                        hover={true}
                        sx={{ cursor: 'pointer' }}
                        onClick={onRowClick(() => handleRowClick(row))}
                    >
                        <TableCell>
                            {getTenantDisplayName(row.tenantSlug)}
                        </TableCell>
                        <TableCell>
                            {getDisplayBookingNumber(row) || notApplicable}
                        </TableCell>
                        <TableCell>
                            {serviceLabel || notApplicable}
                        </TableCell>
                        <TableCell>
                            {formatDateRange(
                                row.startDate,
                                row.endDate,
                                language,
                                notApplicable,
                            )}
                        </TableCell>
                        <TableCell>
                            {row.customerName || notApplicable}
                        </TableCell>
                        <TableCell>{renderStatusPill(row)}</TableCell>
                        <TableCell>{renderIssuePill(row.hasDamageReport)}</TableCell>
                    </TableRow>
                );
            })}
        </MasterListScaffold>
    );
};

export default MasterBookingsPage;
